import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';
import type {
  FilterContext,
  Interaction,
  ScrollTriggerConfig,
  TargetDefinition,
} from '@packages/systems/ix3/schema';
import type {IX3State} from '@packages/systems/ix3/store';

/**
 * Normalize a `wf:class` target value to the flat list of style block ids it
 * references. The Designer's current shape is `StyleBlockId[]`, but two
 * legacy shapes are still in the wild (and survive in IX3 state until
 * `repairMissingFilterBy` migrates them on open):
 *
 * - Legacy single string:  `'sb-1'`
 * - Current array:         `['sb-1', 'sb-2']`
 * - Legacy scoped array:   `[['sb-1', 'sb-2'], scope]`
 *
 * The scoped shape is `[string[], SelectorScope | DirectScope]` (see
 * `TargetSelectorValue` in `ix3-wf-plugins/shared/types.ts`). Treating
 * "array" as "flat id list" would otherwise drop the inner ids on the floor
 * and produce false-positive deletions.
 */
const getClassIdsFromClassValue = (value: unknown): readonly unknown[] => {
  if (typeof value === 'string') return [value];
  if (!Array.isArray(value)) return [];
  return Array.isArray(value[0]) ? value[0] : value;
};

const classValueMatches = (value: unknown, styleBlockId: string): boolean =>
  getClassIdsFromClassValue(value).includes(styleBlockId);

/**
 * Returns true when `target` is a `wf:class` target (or has a `wf:class`
 * filterContext) that references `styleBlockId`.
 *
 * Target tuple shape: `[extensionKey, value, filterContext?]`
 * FilterContext.filterBy shape: `[extensionKey, value]`
 */
const targetReferencesClass = (
  target: unknown,
  styleBlockId: string
): boolean => {
  if (!Array.isArray(target)) return false;
  const [extensionKey, value, filterContext] = target as TargetDefinition;
  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.CLASS &&
    classValueMatches(value, styleBlockId)
  ) {
    return true;
  }
  const filterBy = (filterContext as FilterContext | undefined)?.filterBy;
  if (
    Array.isArray(filterBy) &&
    filterBy[0] === IX3_WF_EXTENSION_KEYS.CLASS &&
    classValueMatches(filterBy[1], styleBlockId)
  ) {
    return true;
  }
  return false;
};

/**
 * Returns true when any of the scroll-trigger config target slots (`pin`,
 * `endTrigger`, `scroller`) references `styleBlockId`. `pin` can also be a
 * boolean — `targetReferencesClass` returns false for non-array input, so
 * it's safe to pass through without a separate guard.
 */
const scrollTriggerReferencesClass = (
  scrollTriggerConfig: ScrollTriggerConfig | undefined,
  styleBlockId: string
): boolean => {
  if (!scrollTriggerConfig) return false;
  const {pin, endTrigger, scroller} = scrollTriggerConfig;
  return (
    targetReferencesClass(pin, styleBlockId) ||
    targetReferencesClass(endTrigger, styleBlockId) ||
    targetReferencesClass(scroller, styleBlockId)
  );
};

/**
 * Returns true when the `wf:class` action property channel references
 * `styleBlockId` via its `selectors` array.
 *
 * Action `properties` is keyed by extension key (`'wf:class'`), and inside
 * that channel by property name (also `'class'`):
 *
 *     properties: { 'wf:class': { class: { selectors, operation } } }
 *
 * This is the shape persisted to MongoDB and read by the runtime at
 * `AnimationCoordinator.ts` via `action.properties[extKey]`. Other plugins
 * (e.g. `wf:transform`) live as sibling keys on `properties`, so the
 * `wf:class` channel can appear on actions whose `targets` are not
 * `wf:class` — that's the case the modal flow originally missed and is the
 * reason this helper exists.
 */
const classActionSelectorsInclude = (
  properties: unknown,
  styleBlockId: string
): boolean => {
  const channel = (
    properties as
      | Record<string, {class?: {selectors?: unknown}} | undefined>
      | undefined
  )?.[IX3_WF_EXTENSION_KEYS.CLASS];
  const selectors = channel?.class?.selectors;
  return Array.isArray(selectors) && selectors.includes(styleBlockId);
};

const interactionReferencesClass = (
  interaction: Interaction,
  styleBlockId: string
): boolean => {
  for (const trigger of interaction.triggers ?? []) {
    const triggerConfig = trigger[1];
    if (
      scrollTriggerReferencesClass(
        triggerConfig?.scrollTriggerConfig,
        styleBlockId
      )
    ) {
      return true;
    }
    if (trigger.length === 3 && targetReferencesClass(trigger[2], styleBlockId))
      return true;
  }
  return false;
};

/**
 * Curried helper that returns true when a style block id is referenced anywhere
 * in the given IX3 state. Symmetric with `isStyleBlockUsedByIx2`.
 *
 * Soft-deleted interactions, timelines, and action presets are skipped — they
 * are excluded from publishing and should not keep style blocks alive.
 *
 * The three top-level records are guarded with `?? {}` to tolerate partial
 * state from cast test mocks; production state from `initialState` always
 * defines them as `{}`.
 *
 * Covered reference locations (all keyed by `StyleBlockId`):
 * - Trigger target: `interactions[*].triggers[*][2]` when `[0] === 'wf:class'`
 * - Trigger target filter: `triggers[*][2][2].filterBy` when `[0] === 'wf:class'`
 * - Scroll trigger config: `triggers[*][1].scrollTriggerConfig.{pin,endTrigger,scroller}` when the target is `'wf:class'`
 * - Action target: `timelines[*].actions[*].targets[*]` when `[0] === 'wf:class'`
 * - Action target filter: same shape, via `filterContext.filterBy`
 * - `wf:class` action selectors: `timelines[*].actions[*].properties['wf:class'].class.selectors`
 * - Action preset class selectors: `actionPresets[*].action.properties['wf:class'].class.selectors`
 */
export const isStyleBlockUsedByIx3 =
  (ix3State: IX3State) =>
  (styleBlockId: string): boolean => {
    for (const interaction of Object.values(ix3State.interactions ?? {})) {
      if (interaction.deleted) continue;
      if (interactionReferencesClass(interaction, styleBlockId)) return true;
    }

    for (const timeline of Object.values(ix3State.timelines ?? {})) {
      if (timeline.deleted) continue;
      for (const action of timeline.actions ?? []) {
        for (const target of action.targets ?? []) {
          if (targetReferencesClass(target, styleBlockId)) return true;
        }
        if (classActionSelectorsInclude(action.properties, styleBlockId)) {
          return true;
        }
      }
    }

    for (const preset of Object.values(ix3State.actionPresets ?? {})) {
      if (preset.deleted) continue;
      if (
        classActionSelectorsInclude(preset.action?.properties, styleBlockId)
      ) {
        return true;
      }
    }

    return false;
  };
